import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Newspaper, EnvelopeSimple, ArrowsClockwise, Plus, ArrowUpRight, Eye, UsersThree, Buildings, Globe, Detective, TrendUp, Flower, Warning, WhatsappLogo, DownloadSimple, ShieldCheck, LinkSimple } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import RichTextEditor from "@/components/RichTextEditor";
import BodyView from "@/components/BodyView";
import TagInput from "@/components/TagInput";
import AudienceSelect from "@/components/AudienceSelect";
import ContentTranslations from "@/components/ContentTranslations";
import ImportInstitutions from "@/components/ImportInstitutions";
import SplinkTab from "@/components/admin/SplinkTab";
import { fileUrl as fileUrlHelper } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminContent() {
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (user && user.role !== "admin") nav("/dashboard"); }, [user, nav]);

  return (
    <main className="mx-auto max-w-5xl px-5 py-8" data-testid="admin-content">
      <h1 className="font-heading text-3xl font-medium">Admin dashboard</h1>
      <p className="mt-1 text-sm text-muted-foreground">Your platform at a glance — plus publishing tools.</p>
      <Analytics />
      <Tabs defaultValue="articles" className="mt-8">
        <TabsList>
          <TabsTrigger value="articles" data-testid="tab-articles"><FileText size={15} className="mr-1.5" /> Articles</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import"><DownloadSimple size={15} className="mr-1.5" /> Import</TabsTrigger>
          <TabsTrigger value="news" data-testid="tab-news"><Newspaper size={15} className="mr-1.5" /> News</TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contact"><EnvelopeSimple size={15} className="mr-1.5" /> Messages</TabsTrigger>
          <TabsTrigger value="memorials" data-testid="tab-memorials"><Flower size={15} className="mr-1.5" /> Memorials</TabsTrigger>
          <TabsTrigger value="suggestions" data-testid="tab-suggestions"><Buildings size={15} className="mr-1.5" /> Institutions</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp"><WhatsappLogo size={15} className="mr-1.5" /> WhatsApp</TabsTrigger>
          <TabsTrigger value="duplicates" data-testid="tab-duplicates"><Detective size={15} className="mr-1.5" /> Duplicates</TabsTrigger>
          <TabsTrigger value="editors" data-testid="tab-editors"><ShieldCheck size={15} className="mr-1.5" /> Editor Requests</TabsTrigger>
          <TabsTrigger value="splink" data-testid="tab-splink"><LinkSimple size={15} className="mr-1.5" /> Links & Analytics</TabsTrigger>
        </TabsList>
        <TabsContent value="articles" className="mt-5"><ArticlesTab /></TabsContent>
        <TabsContent value="import" className="mt-5"><ImportInstitutions /></TabsContent>
        <TabsContent value="news" className="mt-5"><NewsTab /></TabsContent>
        <TabsContent value="contact" className="mt-5"><ContactTab /></TabsContent>
        <TabsContent value="memorials" className="mt-5"><MemorialsTab /></TabsContent>
        <TabsContent value="suggestions" className="mt-5"><InstitutionSuggestionsTab /></TabsContent>
        <TabsContent value="whatsapp" className="mt-5"><WhatsAppTab /></TabsContent>
        <TabsContent value="duplicates" className="mt-5"><DuplicatesTab /></TabsContent>
        <TabsContent value="editors" className="mt-5"><EditorRequestsTab /></TabsContent>
        <TabsContent value="splink" className="mt-5"><SplinkTab /></TabsContent>
      </Tabs>
    </main>
  );
}

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <Icon size={18} weight="duotone" className="text-secondary" />
      </div>
      <div className="mt-2 font-heading text-2xl font-semibold">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => setA(false)); }, []);
  if (!a) return <div className="mt-6 h-24 animate-pulse rounded-2xl bg-muted/40" data-testid="admin-analytics-loading" />;
  const k = a.kpis;
  const maxTrend = Math.max(1, ...a.signups_trend.map((d) => d.count));
  return (
    <div className="mt-6" data-testid="admin-analytics">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <Kpi icon={Eye} label="Website visits" value={k.site_views} sub="across institution sites" testid="kpi-visits" />
        <Kpi icon={UsersThree} label="Members" value={k.users_total} sub={`+${k.users_7} this week`} testid="kpi-users" />
        <Kpi icon={Buildings} label="Institutions" value={k.institutions} sub={`${k.published_sites} published`} testid="kpi-institutions" />
        <Kpi icon={Detective} label="Reconnect posts" value={k.find_open} sub={`${k.find_total} all-time`} testid="kpi-finds" />
        <Kpi icon={Globe} label="Alumni groups" value={k.groups} testid="kpi-groups" />
        <Kpi icon={EnvelopeSimple} label="Messages" value={k.contact_total} sub={`${k.contact_new} unread`} testid="kpi-contact" />
        <Kpi icon={Newspaper} label="News items" value={k.news} testid="kpi-news" />
        <Kpi icon={FileText} label="Articles" value={k.articles} testid="kpi-articles" />
      </div>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2"><TrendUp size={18} weight="duotone" className="text-secondary" /><span className="font-medium">New members · last 7 days</span></div>
          <div className="flex h-28 items-end gap-2">
            {a.signups_trend.map((d) => (
              <div key={d.day} className="flex flex-1 flex-col items-center gap-1">
                <div className="w-full rounded-t bg-secondary/70" style={{ height: `${(d.count / maxTrend) * 90 + 4}px` }} title={`${d.count}`} />
                <span className="text-[10px] text-muted-foreground">{d.day}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2"><Buildings size={18} weight="duotone" className="text-secondary" /><span className="font-medium">Top institutions</span></div>
          <div className="space-y-2">
            {a.top_institutions.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 truncate">{i.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{i.alumni_count} alumni · {i.views} views</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ArticlesTab() {
  const [form, setForm] = useState({ title: "", summary: "", body: "", cover_url: "", tags: [], editions: [] });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mobileView, setMobileView] = useState("write");
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const load = () => api.get("/articles").then((r) => { setItems(r.data.items || []); setTagSuggestions(r.data.tags || []); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!form.title || !form.body) return toast.error("Title and body are required");
    setBusy(true);
    try {
      await api.post("/articles", { ...form, tags: form.tags });
      toast.success("Article published"); setForm({ title: "", summary: "", body: "", cover_url: "", tags: [], editions: [] }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const editor = (
    <Card className="border-border/60 p-5">
      <div className="space-y-3">
        <div><Label>Title</Label><Input className="mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="article-title" /></div>
        <div><Label>Summary</Label><Input className="mt-1" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="article-summary" /></div>
        <div>
          <Label>Cover image</Label>
          <div className="mt-1 flex gap-2">
            <Input value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} placeholder="https://  or upload →" data-testid="article-cover-url" />
            <label className="flex cursor-pointer items-center rounded-md border border-border px-3 text-xs text-muted-foreground hover:border-secondary" data-testid="article-cover-upload">
              Upload
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                const fd = new FormData(); fd.append("file", file);
                try { const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((s) => ({ ...s, cover_url: fileUrlHelper(data.id || data.file_id) })); toast.success("Cover uploaded"); }
                catch { toast.error("Upload failed"); }
              }} />
            </label>
          </div>
        </div>
        <div><Label>Tags</Label><TagInput value={form.tags} onChange={(tags) => setForm({ ...form, tags })} suggestions={tagSuggestions} placeholder="Type to search or create a tag…" testid="article-tags" /></div>
        <AudienceSelect value={form.editions} onChange={(editions) => setForm({ ...form, editions })} testid="article-audience" />
        <div>
          <Label>Body</Label>
          <div className="mt-1.5"><RichTextEditor value={form.body} onChange={(v) => setForm({ ...form, body: v })} placeholder="Write your article…" testid="article-rte" /></div>
        </div>
        <Button onClick={submit} disabled={busy} className="rounded-full gap-1.5" data-testid="article-publish"><Plus size={15} /> {busy ? "Publishing…" : "Publish article"}</Button>
      </div>
    </Card>
  );

  const preview = (
    <Card className="border-border/60 bg-muted/20 p-5" data-testid="article-preview">
      <div className="mb-3 flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Eye size={14} /> Live preview</div>
      {!form.title && !form.body ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Start writing to see your article render here — images, videos and podcasts appear as you embed them.</p>
      ) : (
        <article className="rounded-2xl bg-background p-5 shadow-sm">
          {form.cover_url && <img src={form.cover_url} alt="" className="mb-5 aspect-[16/9] w-full rounded-xl object-cover" />}
          <div className="flex flex-wrap gap-1.5">{form.tags.map((t) => <Badge key={t} variant="outline" className="rounded-full text-xs font-normal">{t}</Badge>)}</div>
          <h1 className="mt-2 font-heading text-3xl font-light tracking-tight">{form.title || "Untitled article"}</h1>
          {form.summary && <p className="mt-2 text-lg text-muted-foreground">{form.summary}</p>}
          <div className="mt-5"><BodyView body={form.body} /></div>
        </article>
      )}
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex gap-2 lg:hidden">
        <button onClick={() => setMobileView("write")} data-testid="article-view-write" className={`flex-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${mobileView === "write" ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground"}`}>Write</button>
        <button onClick={() => setMobileView("preview")} data-testid="article-view-preview" className={`flex-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${mobileView === "preview" ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground"}`}>Preview</button>
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className={mobileView === "write" ? "block" : "hidden lg:block"}>{editor}</div>
        <div className={mobileView === "preview" ? "block" : "hidden lg:block"}>{preview}</div>
      </div>
      <div>
        <p className="mb-2 text-sm font-medium text-muted-foreground">Published articles ({items.length})</p>
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="rounded-xl border border-border/60 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0"><span className="block truncate font-medium">{a.title}</span><span className="text-xs text-muted-foreground">{(a.tags || []).join(", ")}</span></div>
                <a href={`/articles/${a.slug}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-secondary"><ArrowUpRight size={16} /></a>
              </div>
              <ContentTranslations kind="articles" item={a} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NewsTab() {
  const [form, setForm] = useState({ title: "", summary: "", url: "", source: "AlumniPulse", tags: [], editions: [] });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [genCountry, setGenCountry] = useState("");
  const [generating, setGenerating] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const load = () => api.get("/news").then((r) => { setItems(r.data.items || []); setTagSuggestions(r.data.tags || []); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!form.title || !form.url) return toast.error("Title and URL are required");
    setBusy(true);
    try {
      await api.post("/news", { ...form, tags: form.tags });
      toast.success("News item added"); setForm({ title: "", summary: "", url: "", source: "AlumniPulse", tags: [], editions: [] }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const generate = async () => {
    if (!genCountry.trim()) return toast.error("Enter a country");
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/news/generate", { country: genCountry.trim() });
      toast.success(`Fetched ${data.added} new headline${data.added !== 1 ? "s" : ""} for ${data.country}`);
      setGenCountry(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };
  const refresh = async () => {
    setRefreshing(true);
    try { const { data } = await api.post("/news/refresh"); toast.success(`Fetched ${data.added} new items`); load(); }
    catch (e) { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };
  return (
    <div className="space-y-6">
      <Card className="border-secondary/30 bg-secondary/5 p-5" data-testid="news-generate-card">
        <div className="flex items-center gap-2"><Globe size={18} weight="duotone" className="text-secondary" /><span className="font-medium">Collect news by country</span></div>
        <p className="mt-1 text-sm text-muted-foreground">Pull real, localised education & alumni headlines for any country worldwide. They appear on that country's public page automatically.</p>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row">
          <Input value={genCountry} onChange={(e) => setGenCountry(e.target.value)} placeholder="e.g. Germany, Japan, Nigeria" className="rounded-full sm:flex-1" data-testid="news-generate-country" />
          <Button onClick={generate} disabled={generating} className="rounded-full gap-1.5" data-testid="news-generate-submit"><Globe size={15} /> {generating ? "Collecting…" : "Collect news"}</Button>
        </div>
      </Card>
      <Card className="border-border/60 p-5">
        <div className="mb-4 flex items-center justify-between">
          <span className="font-medium">Add an editorial item</span>
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={refresh} disabled={refreshing} data-testid="news-refresh"><ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Fetching…" : "Refresh feed"}</Button>
        </div>
        <div className="space-y-3">
          <div><Label>Headline</Label><Input className="mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-title" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Link URL</Label><Input className="mt-1" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://" data-testid="news-url" /></div>
            <div><Label>Source</Label><Input className="mt-1" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></div>
          </div>
          <div><Label>Tags</Label><TagInput value={form.tags} onChange={(tags) => setForm({ ...form, tags })} suggestions={tagSuggestions} placeholder="Type to search or create a tag…" testid="news-tags" /></div>
          <AudienceSelect value={form.editions} onChange={(editions) => setForm({ ...form, editions })} testid="news-audience" />
          <Button onClick={submit} disabled={busy} className="rounded-full gap-1.5" data-testid="news-publish"><Plus size={15} /> {busy ? "Adding…" : "Add news"}</Button>
        </div>
      </Card>
      <div>
        <p className="mb-2 text-sm font-medium text-muted-foreground">Latest news ({items.length})</p>
        <div className="space-y-2">
          {items.slice(0, 15).map((n) => (
            <div key={n.id} className="rounded-xl border border-border/60 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0"><span className="block truncate font-medium">{n.title}</span><span className="text-xs text-muted-foreground">{n.source}{n.country ? ` · ${n.country}` : ""}</span></div>
                <a href={n.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-secondary"><ArrowUpRight size={16} /></a>
              </div>
              <ContentTranslations kind="news" item={n} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ContactTab() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/contact").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (items === null) return <p className="text-muted-foreground">Loading…</p>;
  if (items.length === 0) return <p className="text-muted-foreground">No messages yet.</p>;
  return (
    <div className="space-y-3" data-testid="contact-messages">
      {items.map((m) => (
        <Card key={m.id} className="border-border/60 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium">{m.name} <span className="text-muted-foreground">·</span> <a href={`mailto:${m.email}`} className="text-secondary hover:underline">{m.email}</a></span>
            <Badge variant="outline" className="rounded-full text-xs">{new Date(m.created_at).toLocaleDateString()}</Badge>
          </div>
          {m.subject && <p className="mt-1 text-sm font-medium">{m.subject}</p>}
          <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{m.message}</p>
        </Card>
      ))}
    </div>
  );
}

const VERIF_OPTS = [
  ["submitted", "Submitted"], ["community_suggested", "Community Suggested"], ["reviewed", "Reviewed"],
  ["institution_verified", "Institution Verified"], ["family_verified", "Family Verified"],
];

function MemorialRow({ m, onChange }) {
  const [busy, setBusy] = useState(false);
  const act = async (payload) => {
    setBusy(true);
    try { await api.post(`/admin/memorials/${m.id}/review`, payload); toast.success("Updated"); onChange(); }
    catch { toast.error("Action failed"); } finally { setBusy(false); }
  };
  const reports = (m.reports || []).length;
  const flags = m.flags || {};
  const livingFlag = (flags.living_person_conflict || []).length > 0 || flags.reported_living;
  const dupeFlag = (flags.duplicate_risk || []).length > 0;
  return (
    <Card className="border-border/60 p-4" data-testid={`admin-memorial-${m.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="font-medium">{m.name}</span>
          <span className="ml-2 text-xs text-muted-foreground">{[m.class_batch, m.institution].filter(Boolean).join(" · ")}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className="rounded-full text-xs">{m.record_type}</Badge>
          <Badge variant="outline" className="rounded-full text-xs">{m.state}</Badge>
          {(m.ai_generated || m.ai_assisted) && <Badge className="rounded-full bg-violet-100 text-xs text-violet-800">AI</Badge>}
          {reports > 0 && <Badge className="rounded-full bg-red-100 text-xs text-red-800">{reports} report{reports > 1 ? "s" : ""}</Badge>}
        </div>
      </div>
      {(livingFlag || dupeFlag) && (
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          {livingFlag && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-800"><Warning size={12} /> Living-person conflict</span>}
          {dupeFlag && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800"><Warning size={12} /> Possible duplicate</span>}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <a href={`/memorials/${m.id}`} target="_blank" rel="noreferrer" className="text-xs text-secondary hover:underline">Preview</a>
        <Select onValueChange={(v) => act({ verification_status: v })}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid={`verif-select-${m.id}`}><SelectValue placeholder="Set verification…" /></SelectTrigger>
          <SelectContent>{VERIF_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {m.state !== "published" && <Button size="sm" className="h-8 rounded-full text-xs" disabled={busy} onClick={() => act({ state: "published" })} data-testid={`publish-${m.id}`}>Publish</Button>}
        {m.state !== "rejected" && <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" disabled={busy} onClick={() => act({ state: "rejected" })} data-testid={`reject-${m.id}`}>Reject</Button>}
        {m.state !== "archived" && <Button size="sm" variant="ghost" className="h-8 rounded-full text-xs text-muted-foreground" disabled={busy} onClick={() => act({ state: "archived" })} data-testid={`archive-${m.id}`}>Archive</Button>}
      </div>
    </Card>
  );
}

function MemorialsTab() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("pending_review");
  const load = () => api.get(`/admin/memorials${filter ? `?state=${filter}` : ""}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [filter]);
  return (
    <div className="space-y-4" data-testid="admin-memorials-tab">
      <div className="flex flex-wrap gap-2">
        {[["pending_review", "Pending review"], ["published", "Published"], ["rejected", "Rejected"], ["archived", "Archived"], ["", "All"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`admin-mem-filter-${v || "all"}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === v ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>{l}</button>
        ))}
      </div>
      {items === null ? <p className="text-muted-foreground">Loading…</p>
        : items.length === 0 ? <p className="text-muted-foreground">No memorials in this view.</p>
          : <div className="space-y-3">{items.map((m) => <MemorialRow key={m.id} m={m} onChange={load} />)}</div>}
    </div>
  );
}


const SUG_STATUSES = [
  ["submitted", "Submitted"], ["researching", "Researching"], ["information_needed", "Info needed"],
  ["under_review", "Under review"], ["approved", "Approved"], ["added", "Added"],
  ["duplicate", "Duplicate"], ["not_verified", "Not verified"], ["archived", "Archived"],
];

function SuggestionRow({ s, onChange }) {
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const r = s.research;

  const act = async (fn, key) => {
    setBusy(key);
    try { await fn(); toast.success("Updated"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };
  const research = () => act(() => api.post(`/admin/institution-suggestions/${s.id}/research`), "research");
  const approve = () => act(() => api.post(`/admin/institution-suggestions/${s.id}/approve`), "approve");
  const setStatus = (status, n = "") => act(() => api.post(`/admin/institution-suggestions/${s.id}/status`, { status, note: n }), status);

  const links = [["Website", s.website], ["Facebook", s.facebook], ["LinkedIn", s.linkedin], ["Wikipedia", s.wikipedia], ["Other", s.other_links]].filter(([, v]) => v);

  return (
    <Card className="border-border/60 p-4" data-testid={`admin-suggestion-${s.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-lg font-medium">{s.name}</h3>
            <Badge className="rounded-full font-normal">{(SUG_STATUSES.find(([v]) => v === s.status) || [, s.status])[1]}</Badge>
            {s.credit_opt_in && <Badge variant="outline" className="rounded-full text-xs font-normal">Credit OK</Badge>}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{[s.type, s.city || s.locality, s.state, s.country].filter(Boolean).join(" · ")}</p>
          <p className="mt-1 text-xs text-muted-foreground">By {s.submitter_name || "—"} ({s.submitter_email || "no email"}){s.source_relationship ? ` · ${s.source_relationship}` : ""}</p>
        </div>
      </div>

      {(s.other_names || s.former_name || s.founded || s.history || s.extra || s.head) && (
        <div className="mt-2 space-y-1 text-sm">
          {s.other_names && <p><span className="text-muted-foreground">Also known as:</span> {s.other_names}</p>}
          {s.former_name && <p><span className="text-muted-foreground">Former:</span> {s.former_name}{s.new_name ? ` → ${s.new_name}` : ""}</p>}
          {s.founded && <p><span className="text-muted-foreground">Founded:</span> {s.founded}{s.operating_status ? ` · ${s.operating_status}${s.closure_status ? " (" + s.closure_status + ")" : ""}` : ""}</p>}
          {s.head && <p><span className="text-muted-foreground">Head:</span> {s.head}</p>}
          {s.history && <p className="text-muted-foreground">{s.history}</p>}
          {s.extra && <p className="text-muted-foreground italic">{s.extra}</p>}
        </div>
      )}

      {links.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-3 text-xs">
          {links.map(([l, u]) => <a key={l} href={u} target="_blank" rel="noreferrer" className="text-secondary hover:underline">{l} ↗</a>)}
        </div>
      )}

      {s.duplicate_matches?.length > 0 && (
        <div className="mt-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
          <strong>Possible duplicates:</strong> {s.duplicate_matches.map((d) => d.name).join(", ")}
        </div>
      )}

      {r && (
        <div className="mt-3 rounded-xl border border-secondary/20 bg-secondary/5 p-3 text-sm" data-testid="suggestion-research">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="rounded-full bg-secondary/15 font-normal text-secondary">AI research</Badge>
            <span className="text-xs text-muted-foreground">Verification: <strong>{r.verification}</strong> · Confidence: <strong>{r.confidence}</strong></span>
          </div>
          <p className="mt-1.5 text-muted-foreground">{r.summary}</p>
          {r.sources?.length > 0 && <p className="mt-1 text-xs text-muted-foreground">Sources: {r.sources.join(", ")}</p>}
          {r.duplicate_risk && <p className="mt-1 text-xs text-muted-foreground">Duplicate risk: {r.duplicate_risk}</p>}
          {r.recommendation && <p className="mt-1 text-xs font-medium">Recommendation: {r.recommendation}</p>}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={research} disabled={busy === "research"} data-testid={`research-${s.id}`}>{busy === "research" ? "Researching…" : (r ? "Re-run AI research" : "AI research")}</Button>
        {!s.institution_id && <Button size="sm" className="rounded-full" onClick={approve} disabled={busy === "approve"} data-testid={`approve-${s.id}`}>{busy === "approve" ? "Adding…" : "Approve & create"}</Button>}
        {s.institution_id && <a href={`/institutions/${s.institution_id}`} target="_blank" rel="noreferrer" className="text-xs text-secondary hover:underline">View institution ↗</a>}
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => setShowNote((v) => !v)}>Set status</Button>
      </div>

      {showNote && (
        <div className="mt-2 space-y-2 rounded-xl bg-muted/30 p-3">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to the submitter (e.g. what info is needed)" className="text-sm" data-testid={`note-${s.id}`} />
          <div className="flex flex-wrap gap-1.5">
            {SUG_STATUSES.map(([v, l]) => (
              <button key={v} onClick={() => setStatus(v, note)} disabled={!!busy}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${s.status === v ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>{l}</button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function InstitutionSuggestionsTab() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const load = () => { setItems(null); api.get("/admin/institution-suggestions", { params: { status: filter } }).then((r) => setItems(r.data)).catch(() => setItems([])); };
  useEffect(load, [filter]);
  return (
    <div className="space-y-6" data-testid="suggestions-tab">
      <CorrectionsPanel />
      <div>
        <h3 className="mb-2 font-heading text-lg font-medium">Suggested institutions</h3>
        <div className="flex flex-wrap gap-2">
          {[["", "All"], ...SUG_STATUSES].map(([v, l]) => (
            <button key={v || "all"} onClick={() => setFilter(v)} data-testid={`sug-filter-${v || "all"}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === v ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>{l}</button>
          ))}
        </div>
        {items === null ? <p className="mt-3 text-muted-foreground">Loading…</p>
          : items.length === 0 ? <p className="mt-3 text-muted-foreground">No institution suggestions in this view.</p>
            : <div className="mt-3 space-y-3">{items.map((s) => <SuggestionRow key={s.id} s={s} onChange={load} />)}</div>}
      </div>
    </div>
  );
}

function CorrectionsPanel() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/institution-corrections", { params: { state: "open" } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const resolve = async (id, action, field_value = null) => {
    try { await api.post(`/admin/institution-corrections/${id}/resolve`, { action, field_value }); toast.success(action === "apply" ? "Applied" : "Dismissed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  if (items === null) return <p className="text-muted-foreground">Loading corrections…</p>;
  const applicable = ["name", "location", "website", "founded", "type"];
  return (
    <div data-testid="corrections-panel">
      <h3 className="mb-2 font-heading text-lg font-medium">Correction requests <span className="text-sm font-normal text-muted-foreground">({items.length} open)</span></h3>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No open correction requests.</p> : (
        <div className="space-y-2">
          {items.map((c) => (
            <Card key={c.id} className="border-border/60 p-4" data-testid={`correction-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <a href={`/institutions/${c.institution_id}`} target="_blank" rel="noreferrer" className="font-medium text-secondary hover:underline">{c.institution_name}</a>
                    <Badge variant="outline" className="rounded-full text-xs font-normal capitalize">{c.field}</Badge>
                  </div>
                  {c.suggested_value && <p className="mt-1 text-sm">Suggested: <strong>{c.suggested_value}</strong></p>}
                  {c.note && <p className="mt-0.5 text-sm text-muted-foreground">{c.note}</p>}
                  <p className="mt-1 text-xs text-muted-foreground">By {c.reporter_name || "anonymous"}{c.contact ? ` · ${c.contact}` : ""}</p>
                </div>
                <div className="flex gap-2">
                  {applicable.includes(c.field) && c.suggested_value && (
                    <Button size="sm" className="rounded-full" onClick={() => resolve(c.id, "apply")} data-testid={`correction-apply-${c.id}`}>Apply</Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => resolve(c.id, "dismiss")} data-testid={`correction-dismiss-${c.id}`}>Dismiss</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DuplicatesTab() {
  const [data, setData] = useState(null);
  const [keep, setKeep] = useState({});
  const [busy, setBusy] = useState(null);
  const load = () => { setData(null); api.get("/admin/institutions/duplicates").then((r) => { setData(r.data); const k = {}; (r.data.clusters || []).forEach((c, i) => { k[i] = c[0].id; }); setKeep(k); }).catch(() => setData(false)); };
  useEffect(() => { load(); }, []);
  const merge = async (ci, cluster) => {
    const keepId = keep[ci];
    const mergeIds = cluster.filter((x) => x.id !== keepId).map((x) => x.id);
    if (!mergeIds.length) return;
    setBusy(ci);
    try {
      const { data: res } = await api.post("/admin/institutions/merge", { keep_id: keepId, merge_ids: mergeIds });
      toast.success(`Merged ${res.merged} duplicate${res.merged === 1 ? "" : "s"} into the kept record`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
    finally { setBusy(null); }
  };
  if (data === null) return <p className="text-muted-foreground">Scanning for duplicates…</p>;
  if (data === false) return <p className="text-muted-foreground">Could not load duplicates.</p>;
  if (!data.clusters.length) return <p className="text-muted-foreground" data-testid="dupes-empty">No near-duplicate institutions found. 🎉</p>;
  return (
    <div className="space-y-4" data-testid="admin-duplicates-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{data.total_clusters} possible duplicate group{data.total_clusters === 1 ? "" : "s"} found. Pick the record to keep — the others merge into it (members, groups and links move over).</p>
        <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={load} data-testid="dupes-refresh"><ArrowsClockwise size={14} /> Rescan</Button>
      </div>
      {data.clusters.map((cluster, ci) => (
        <Card key={ci} className="border-border/60 p-4" data-testid={`dupe-cluster-${ci}`}>
          <div className="space-y-2">
            {cluster.map((it) => (
              <label key={it.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${keep[ci] === it.id ? "border-green-500 bg-green-50" : "border-slate-200 hover:border-slate-300"}`}>
                <input type="radio" name={`keep-${ci}`} checked={keep[ci] === it.id} onChange={() => setKeep((k) => ({ ...k, [ci]: it.id }))} data-testid={`dupe-keep-${it.id}`} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground">{it.name}</p>
                  <p className="text-xs text-muted-foreground">{[it.type, it.location, it.country].filter(Boolean).join(" · ")}{it.source ? ` · ${it.source}` : ""}</p>
                </div>
                {keep[ci] === it.id && <Badge className="rounded-full bg-green-100 text-xs font-normal text-green-800">Keep</Badge>}
              </label>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <Button size="sm" className="rounded-full gap-1.5" disabled={busy === ci} onClick={() => merge(ci, cluster)} data-testid={`dupe-merge-${ci}`}>
              {busy === ci ? "Merging…" : `Merge ${cluster.length - 1} into kept`}
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function EditorRequestsTab() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => { setRows(null); api.get("/admin/claims").then((r) => setRows(r.data || [])).catch(() => setRows(false)); };
  useEffect(() => { load(); }, []);
  const act = async (id, kind) => {
    setBusy(id);
    try { await api.post(`/admin/claims/${id}/${kind}`); toast.success(kind === "approve" ? "Approved — access granted" : "Request rejected"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  };
  if (rows === null) return <p className="text-muted-foreground">Loading requests…</p>;
  if (rows === false) return <p className="text-muted-foreground">Could not load requests.</p>;
  if (!rows.length) return (
    <div className="space-y-3" data-testid="editor-requests-empty">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground">No pending editor requests. 🎉</p>
        <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={load} data-testid="editor-requests-refresh"><ArrowsClockwise size={14} /> Refresh</Button>
      </div>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="admin-editor-requests-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{rows.length} pending request{rows.length === 1 ? "" : "s"} for editor / manager access.</p>
        <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={load} data-testid="editor-requests-refresh"><ArrowsClockwise size={14} /> Refresh</Button>
      </div>
      {rows.map((r) => (
        <Card key={r.id} className="border-border/60 p-4" data-testid={`editor-request-row-${r.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{r.institution_name}</span>
                <Badge variant="secondary" className="rounded-full text-[11px] font-normal">{r.target_type === "group" ? "Alumni group" : "Institution"}</Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">Requested by <strong>{r.user_name}</strong>{r.role_title ? ` · ${r.role_title}` : ""}</p>
              {r.work_email && <p className="text-xs text-muted-foreground">Contact: {r.work_email}</p>}
              {r.evidence && <p className="mt-1 text-xs italic text-muted-foreground">“{r.evidence}”</p>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full" disabled={busy === r.id} onClick={() => act(r.id, "approve")} data-testid={`editor-approve-${r.id}`}>Approve</Button>
              <Button size="sm" variant="outline" className="rounded-full" disabled={busy === r.id} onClick={() => act(r.id, "reject")} data-testid={`editor-reject-${r.id}`}>Reject</Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function WhatsAppTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("awaiting");
  const load = () => { setData(null); api.get("/admin/groups/whatsapp").then((r) => setData(r.data)).catch(() => setData(false)); };
  useEffect(() => { load(); }, []);
  if (data === null) return <p className="text-muted-foreground">Loading…</p>;
  if (data === false) return <p className="text-muted-foreground">Could not load WhatsApp overview.</p>;
  const { groups, threshold, summary } = data;
  const shown = groups.filter((g) => {
    if (filter === "ready") return !g.connected && g.interest_count >= threshold;
    if (filter === "awaiting") return !g.connected && g.interest_count > 0;
    if (filter === "connected") return g.connected;
    return true;
  });
  const statusPill = (g) => {
    if (g.connected) return <Badge className="rounded-full bg-green-100 text-xs font-normal text-green-800">Connected{g.status ? ` · ${g.status}` : ""}</Badge>;
    if (g.interest_count >= threshold) return <Badge className="rounded-full bg-amber-100 text-xs font-normal text-amber-800">Ready to set up</Badge>;
    return <Badge variant="outline" className="rounded-full text-xs font-normal text-muted-foreground">No group yet</Badge>;
  };
  return (
    <div className="space-y-4" data-testid="admin-whatsapp-tab">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi icon={UsersThree} label="Groups" value={summary.total} testid="wa-kpi-total" />
        <Kpi icon={WhatsappLogo} label="Connected" value={summary.connected} testid="wa-kpi-connected" />
        <Kpi icon={TrendUp} label="Awaiting interest" value={summary.awaiting} testid="wa-kpi-awaiting" />
        <Kpi icon={Warning} label={`Ready (≥${threshold})`} value={summary.ready} sub="nudge managers" testid="wa-kpi-ready" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {[["ready", `Ready to set up (≥${threshold})`], ["awaiting", "Has interest"], ["connected", "Connected"], ["all", "All"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`wa-filter-${v}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === v ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>{l}</button>
        ))}
        <Button size="sm" variant="outline" className="ml-auto rounded-full gap-1.5" onClick={load} data-testid="wa-refresh"><ArrowsClockwise size={14} /> Refresh</Button>
      </div>
      {shown.length === 0 ? <p className="text-muted-foreground">No groups in this view.</p> : (
        <div className="space-y-2">
          {shown.map((g) => (
            <Card key={g.id} className="border-border/60 p-4" data-testid={`wa-group-${g.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <a href={`/groups/${g.id}`} target="_blank" rel="noreferrer" className="font-medium text-secondary hover:underline">{g.name}</a>
                    {statusPill(g)}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{[g.institution, `${g.member_count} members`].filter(Boolean).join(" · ")}{g.nudged_at ? " · manager nudged" : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-heading text-lg font-semibold">{g.interest_count}</div>
                    <div className="text-[10px] text-muted-foreground">interested</div>
                  </div>
                  <a href={`/groups/${g.id}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-secondary" data-testid={`wa-open-${g.id}`}><ArrowUpRight size={16} /></a>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}